import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { getBaselineList, getNormalizeDefaultValues } from "api/report";

import getLogger from "utils/logger.util";
import { AppStrConstant, BASELINE_TABLE_STRINGS, valueStrings } from "constants/constantStrings";
import { LOCAL_TIME_ZONE, WIDGET_TITLE_INPUT_MAXIMUM } from "constants/globalConstants";

import AddBaseLineModal from "components/AddBaseLineModal";
import CustomModal from "components/CustomModal";
import Dropdown from "components/Dropdown";
import DupontButton from "components/DupontButton";
import ErrorLabel from "components/ErrorLabel";
import InputBox from "components/InputBox";
import { TextFieldValidator } from "components/InputBox/TextFieldValidator";
import createNotification, { WARNING_NOTIFICATION } from "components/Notifications";
import TagDropdown from "components/TagDropdown";

import AddIcon from "assets/Other/plus-blue.svg";

import NormalizedUtil from "./normalizedSeriesForm.util";

import "./index.css";

export default function NormalizedWidgetForm(props) {
  const Logger = getLogger("NormalizedWidgetForm");
  const { Header, currentLayout, setIsReportRerendering, isEdit, isChartView, tagsConfig, Attributes, onWidgetAction } = props.data;
  const {isNormChart } = props;
  const { NormalizedWidgetFormStrings, WidgetStrings, DROPDOWN_STRINGS, InputErrorStrings, ToasterStatusStrings } = AppStrConstant;
  const state = useSelector(state => state);
  const { isTier1 } = state.user;
  const { tagsAsObj, normalizedTags } = state.tags;
  let NormalizationFilters = props.data.NormalizationFilters || state.chartData.NormalizationFilters;
  const ALL_TAGS = isChartView ? [...normalizedTags] : Object.values(tagsAsObj);
  const tagsConfigList = isChartView ? tagsConfig : NormalizedUtil.getTagsConfig();
  const [openModal, setOpenModal] = useState(true);
  const [isFormInvalid, setIsFormInvalid] = useState(true);
  const Tags = Attributes ? ALL_TAGS.filter(normalisedTag => Attributes.SystemTag.some(tag => normalisedTag.TagId === tag)) : null;
  const groupBy = option => option?.Group;
  const [onlyNormalizedTags, setOnlyNormalizedTags] = useState(state.tags.normalizedTags);

  const defaultState = {
    Tag1: tagsConfigList && tagsConfigList[0].value,
    ...NormalizedUtil.getWidgetDefaultState(Header, Tags, Attributes?.Comments)
  };

  const [widgetData, setWidgetData] = useState(defaultState);
  const [inputBoxesData, setInputBoxesData] = useState([]);
  const [baselines, setBaselines] = useState(null);
  const [normalizationTagsOptions, setNormalizationTagsOptions] = useState(filterDefaultTags());
  const [isBaselineFormOpen, setIsBaselineFormOpen] = useState(false);
  const [baselineTimezone] = useState(LOCAL_TIME_ZONE);
  const [copyBaselineId, setCopyBaselineId] = useState(null)

  function filterDefaultTags() {
    let tags = ALL_TAGS;
    if (tagsConfigList) {
      const existingTags = tagsConfigList.filter(config => Boolean(config.value)).map(({ value }) => value.TagName);
      tags = ALL_TAGS.filter(({ TagName }) => !existingTags.includes(TagName));
    }
    return tags;
  }

  const getFormInputsData = filtersData => {
    const config = NormalizedUtil.getInputBoxsConfig(filtersData);
    return config.map(item => {
      const { id } = item;
      item.label = NormalizedWidgetFormStrings[id].replace("%", tagsAsObj[id].Units);
      return item;
    });
  };

  useEffect(() => {
    if (isEdit && !isChartView) {
      filterSelectedTags(widgetData);
    }
  }, []);

  useEffect(() => {
    if (!isChartView) {
      let data = { ...NormalizedUtil.getWidgetDefaultState(Header, Tags, Attributes?.Comments) };

      if (Attributes) {
        const { Baseline, FeedFlowH, FeedFlowL, RecoveryH, RecoveryL, RejectConductivityH, RejectConductivityL } = Attributes;
        NormalizationFilters = { Baseline, FeedFlowH, FeedFlowL, RecoveryH, RecoveryL, RejectConductivityH, RejectConductivityL };
        if(isNormChart){
        const inputsConfig = getFormInputsData(NormalizationFilters);
        setInputBoxesData(inputsConfig);
        }
      }
      if (baselines) {
        const Baseline = getBaselineVal(baselines);
        if (Baseline) data = { ...data, Baseline };
      }
      setWidgetData(data);
      const inputsConfig = getFormInputsData(NormalizationFilters);
      setInputBoxesData(inputsConfig);
    }
  }, [props.data]);

  const fetchBaselines = async () => {
    try {
      const response = await getBaselineList();
      if (response.status >= 200 && response.status < 300) {
        let BaselineList = response.data;
        BaselineList = BaselineList?.map(baseline => {
          const group = baseline.Type === BASELINE_TABLE_STRINGS.auto ? BASELINE_TABLE_STRINGS.automated : BASELINE_TABLE_STRINGS.manual;
          return { ...baseline, Group: group };
        });

        setBaseline(BaselineList);
        if (props.setbaselinesList) {
          props.setbaselinesList(BaselineList);
        }
      }
    } catch (err) {
      Logger.error("fetchBaselines error: ", err);
    }
  };

  const fetchNormalizationFilters = async () => {
    try {
      const response = await getNormalizeDefaultValues();
      if (response.data) {
        const { FeedFlowH, FeedFlowL, RecoveryH, RecoveryL, RejectConductivityH, RejectConductivityL } = response.data;
        NormalizationFilters = { FeedFlowH, FeedFlowL, RecoveryH, RecoveryL, RejectConductivityH, RejectConductivityL };
        const inputsConfig = getFormInputsData(NormalizationFilters);
        setInputBoxesData(inputsConfig);
      }
    } catch (err) {
      Logger.log("fetchNormalizationFilters error: ", err);
    }
  };

  useEffect(() => {
    updateUnitsValues(NormalizationFilters);
    fetchBaselines();
    const { Baseline } = NormalizationFilters;
    if (!Baseline && !isNormChart) {
      fetchNormalizationFilters();
    }
    if (isChartView) {
      setWidgetData({ ...widgetData, Tag1: tagsConfigList && tagsConfigList[0].value });
    }
  }, []);

  const updateUnitsValues = (FilterLimits, Errors) => {
    if (Errors?.length) {
      createNotification(WARNING_NOTIFICATION, NormalizedWidgetFormStrings.filterFetchErr, ToasterStatusStrings.warning);
    } else {
      const inputsConfig = getFormInputsData(NormalizationFilters);
      setInputBoxesData(inputsConfig);
    }
  };

  const filterSelectedTags = formData => {
    if (tagsConfigList) {
      const tagsVal = tagsConfigList.map((config, idx) => formData[`Tag${idx + 1}`]?.TagName).filter(tag => tag);
      let updatedArr = ALL_TAGS;
      if (tagsVal && tagsVal.length) {
        updatedArr = updatedArr.filter(option => !tagsVal.includes(option.TagName));
      }
      setNormalizationTagsOptions(updatedArr);
      const updatedNormalizedTags = normalizedTags.filter(tag => !tagsVal.includes(tag.TagName));
      setOnlyNormalizedTags(updatedNormalizedTags);
    }
  };

  const getBaselineVal = baselineList => {
    const { Baseline } = NormalizationFilters;
    if (baselineList && Baseline) {
      return baselineList.find(b => b.Id === Baseline.toString());
    }
  };

  const setBaseline = BaselineList => {
    let filteredBaselineList = BaselineList;
    // if (isTier1) {
    //   filteredBaselineList = BaselineList.filter(baseline => baseline.Type !== "auto");
    // }
    setBaselines(filteredBaselineList);
    const Baseline = getBaselineVal(BaselineList);
    if (Baseline) {
      setWidgetData({ ...widgetData, Baseline });
      if (!isEdit) validateForm({ ...widgetData, Baseline });
    }
  };

  const closeModal = closeType => {
    setOpenModal(false);
    props.onClose(closeType);
  };

  const onTagChange = (idx, value) => {
    const formData = { ...widgetData, [`Tag${idx + 1}`]: value };
    filterSelectedTags(formData);
    validateForm(formData);
  };

  const onTitleChange = Header => {
    const formData = { ...widgetData, Header };
    validateForm(formData);
  };

  const onBaselineChange = Baseline => validateForm({ ...widgetData, Baseline });

  const onInputBlur = event => {
    const tempData = [...inputBoxesData];
    const { item } = NormalizedUtil.getItemByName(event.target.name, tempData);
    validateUnitsRange(item);
    setInputBoxesData(tempData);
  };

  const onFilterChange = (value, name) => {
    if (!isNaN(value)) {
      const tempData = [...inputBoxesData];
      const { item, childKey } = NormalizedUtil.getItemByName(name, tempData);
      item.child[childKey].value = value;
      setInputBoxesData(tempData);
    }
    validateForm(widgetData);
  };

  const checkUnitsError = item => item?.child.some(child => !child.value && +child.value !== 0);

  const validateUnitsRange = parent => {
    const child0Val = parent.child[0].value;
    const child1Val = parent.child[1].value;
    parent.error = null;
    let isFieldsInvalid = false;
    if (!child0Val || !child1Val) {
      isFieldsInvalid = true;
    } else {
      parent.error = +child0Val > +child1Val ? NormalizedWidgetFormStrings.filterError : null;
      isFieldsInvalid = Boolean(parent.error);
    }
    return isFieldsInvalid;
  };

  const isInvalidUnits = () => {
    let isInvalidUnit = false;
    const unitsData = inputBoxesData.map(item => {
      const isInvalid = validateUnitsRange(item) || checkUnitsError(item);
      if (!isInvalidUnit) {
        isInvalidUnit = isInvalid;
      }
      return item;
    });
    if (isInvalidUnit) {
      setInputBoxesData(unitsData);
    }
    return isInvalidUnit;
  };

  const validateForm = formData => {
    if(isNormChart){
    const { Baseline } = formData;
    let isInvalidData =  !Baseline;
    setIsFormInvalid(isInvalidData);
    setWidgetData(formData);
    setCopyBaselineId(Baseline)
    }
    else{
    const { Header, Tag1, Baseline } = formData;
    const invalidUnits = isInvalidUnits();
    let isInvalidData = !Header || !Baseline || invalidUnits || Header.trim().length === 0;
    //DONOT do validation for Normalized tag when Chart Edit View else validate
    if (!isChartView || !isEdit) isInvalidData = !Tag1 || isInvalidData;
    setIsFormInvalid(isInvalidData);
    setWidgetData(formData);
    }
  };

  const onFormSubmit = async () => {
    setIsFormInvalid(true);
    const filtersData = NormalizedUtil.parseInputVal(inputBoxesData);
    const payload = NormalizedUtil.getPayload(widgetData, currentLayout, filtersData, isChartView);
    if (!isChartView) {
      if (isEdit) {
        payload.Id = props.data.Id;
      } else {
        //  await addWidgetApi(payload);
        setIsReportRerendering(true);
        setIsFormInvalid(false);
        onWidgetAction("ADD_WIDGET", payload);
      }
    }
    closeModal(payload);
  };

  const onAddNewBaseline = () => {
    if (props.setCreateBaseline && props.isChartDisplayed) props.setCreateBaseline(true);
    else setIsBaselineFormOpen(true);
  };

  const renderInputBoxRow = inputCols =>
    inputCols.child.map((input, idx) => (
      <div className="filter-input-width" key={idx}>
        {renderTextField(input.name, input.value, input.label, onInputBlur)}
      </div>
    ));

  const renderTextField = (inputName, value, placeholder, blurHandler) => (
    <InputBox
      name={inputName}
      requiredMsg={InputErrorStrings.required}
      value={value}
      placeholder={placeholder}
      blurHandler={blurHandler}
      onChange={onFilterChange}
    />
  );

  const getTitle = () => {
    const title = isEdit ? NormalizedWidgetFormStrings.editTitle : NormalizedWidgetFormStrings.addTitle;
    const postfix = isChartView ? NormalizedWidgetFormStrings.tag : NormalizedWidgetFormStrings.series;
    return `${title} ${postfix}`;
  };

  const getDescription = useCallback(() => {
    let desc = NormalizedWidgetFormStrings.descriptionReport;
    if (isChartView) {
      desc = isEdit ? NormalizedWidgetFormStrings.descriptionChartEdit : NormalizedWidgetFormStrings.descriptionChartAdd;
    }
    return desc;
  }, []);

  const isBackBtnHidden = () => isEdit || isChartView;
  const getConfirmBtnText = () => {
    let text = "";
    if (isEdit) {
      text = isChartView ? valueStrings.save : WidgetStrings.editWidget;
    } else {
      text = isChartView ? WidgetStrings.addTag : WidgetStrings.addWidget;
    }
    return text;
  };

  const isBaselinesFieldDisabled = () => !baselines || baselines.length === 0;

  return (
    <>
      {openModal && (
        <CustomModal
          show={openModal}
          title={getTitle()}
          confirmBtnText={getConfirmBtnText()}
          description={getDescription()}
          secondaryDescription={valueStrings.mandatoryField}
          close={closeModal}
          confirmBtnAction={onFormSubmit}
          confirmDisabled={isFormInvalid}
          isBackBtnHidden={isBackBtnHidden()}
          id="customModal"
        >
          <div className="widget-form-container">
            {!isChartView && !isNormChart&& (
              <div className="form-group">
                <label className="bodyMainHeadingText" htmlFor="widgetTitle">
                  {WidgetStrings.widgetTitle}*
                </label>
                <InputBox
                  name="widgetTitle"
                  value={widgetData.Header}
                  onChange={onTitleChange}
                  placeholder={WidgetStrings.titlePlaceholder}
                  requiredMsg={InputErrorStrings.required}
                  validator={TextFieldValidator.ALPHANUMERIC_WITH_SPECIAL_CHARS}
                  maxLength={WIDGET_TITLE_INPUT_MAXIMUM}
                />
              </div>
            )}
            {!isNormChart&& tagsConfigList &&
              tagsConfigList.map((config, idx) => (
                <div className="form-group" key={idx}>
                  <label className="bodyMainHeadingText">
                    {WidgetStrings.selectTag}
                    {config.required && <span>*</span>}
                  </label>
                  <TagDropdown
                    dropDownID={config.id}
                    tagValue={widgetData[`Tag${idx + 1}`]}
                    tagList={config.required ? onlyNormalizedTags : normalizationTagsOptions}
                    requiredMsg={config.required && InputErrorStrings.required}
                    onTagChange={(value, reason) => onTagChange(idx, value, reason)}
                  />
                </div>
              ))}

            {!isNormChart&& inputBoxesData &&
              inputBoxesData.map((inputBox, idx) => (
                <div className="form-group" key={`input-${idx}`}>
                  <label className="bodyMainHeadingText">
                    {inputBox.label}
                    <span>*</span>
                  </label>
                  <div className="flex-between"> {renderInputBoxRow(inputBox)} </div>
                  {inputBox.error && <ErrorLabel message={inputBox.error} />}
                </div>
              ))}

            <div className="form-group">
              <Dropdown
                value={isNormChart? copyBaselineId : widgetData.Baseline}
                labelText={`${NormalizedWidgetFormStrings.selectBaseline}*`}
                options={baselines}
                onChange={onBaselineChange}
                disabled={isBaselinesFieldDisabled()}
                groupBy={groupBy}
                placeholder={DROPDOWN_STRINGS.chooseOptionStr}
                getOptionLabel={option => option.Name}
              />
            </div>

            <div className="form-group">
              <DupontButton type="tertiary" onClick={onAddNewBaseline}>
                {NormalizedWidgetFormStrings.addNewBaseline}
                {<img className="padding8" src={AddIcon} />}
              </DupontButton>
            </div>
          </div>
        </CustomModal>
      )}
      {isBaselineFormOpen && (
        <AddBaseLineModal
          setIsBaselineFormOpen={setIsBaselineFormOpen}
          baselines={baselines}
          baselineTimezone={baselineTimezone}
          setBaselines={setBaselines}
        />
      )}
    </>
  );
}
